// ── ExcelImport — 홈택스 엑셀 일괄 등록 ──
import React, { useState, useEffect } from 'react';
import {
  parseHomeTaxExcel, FORMAT_LABEL,
  isDuplicate, candidateToTransaction,
} from '../utils/excelImportMapper';
import { CATEGORY_MAJOR, CATEGORY_SUB } from '../data/taxConstants';
import { L, M, Lv } from '../i18n/L';   // V160 중국어

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function ExcelImport({ isOpen, onClose, onSave, existingTransactions = [] }) {
  const [files, setFiles] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFiles([]); setCandidates([]); setParsing(false); setSaving(false); setDragOver(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFiles = async (fileList) => {
    const list = Array.from(fileList);
    if (list.length === 0) return;
    setParsing(true);
    const newParsed = [], newCands = [];
    for (const f of list) {
      try {
        const parsed = await parseHomeTaxExcel(f);
        newParsed.push({ file: f, parsed, status: 'done' });
        parsed.candidates.forEach((c, idx) => {
          const dup = isDuplicate(c, existingTransactions);
          newCands.push({
            ...c,
            _id: `${f.name}_${idx}_${Date.now()}`,
            _fileName: f.name,
            _duplicate: dup,
            _included: !dup,
          });
        });
      } catch (e) {
        newParsed.push({ file: f, parsed: null, status: 'error', error: e.message });
      }
    }
    setFiles(prev => [...prev, ...newParsed]);
    setCandidates(prev => [...prev, ...newCands]);
    setParsing(false);
  };

  const toggleIncluded = (id) => {
    setCandidates(prev => prev.map(c => c._id === id ? { ...c, _included: !c._included } : c));
  };
  const updateCategory = (id, major, sub) => {
    setCandidates(prev => prev.map(c => c._id === id ? { ...c, category: { major, sub } } : c));
  };
  const bulkUpdateByPartner = (partnerKey, major, sub) => {
    setCandidates(prev => prev.map(c => {
      const ck = (c.partnerBizNumber || c.partnerName || '').trim();
      return ck === partnerKey ? { ...c, category: { major, sub } } : c;
    }));
  };
  const bulkToggleByPartner = (partnerKey, included) => {
    setCandidates(prev => prev.map(c => {
      const ck = (c.partnerBizNumber || c.partnerName || '').trim();
      return ck === partnerKey ? { ...c, _included: included } : c;
    }));
  };

  const partnerGroups = (() => {
    const map = new Map();
    for (const c of candidates) {
      const key = (c.partnerBizNumber || c.partnerName || '').trim();
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, { key, partnerName: c.partnerName, count: 0, includedCount: 0, currentCat: c.category, mixed: false });
      }
      const g = map.get(key);
      g.count += 1;
      if (c._included) g.includedCount += 1;
      if (g.currentCat && c.category && (g.currentCat.major !== c.category.major || g.currentCat.sub !== c.category.sub)) {
        g.mixed = true;
      }
    }
    return Array.from(map.values()).filter(g => g.count >= 2).sort((a, b) => b.count - a.count);
  })();

  const stats = {
    total: candidates.length,
    duplicates: candidates.filter(c => c._duplicate).length,
    included: candidates.filter(c => c._included).length,
    deductible: candidates.filter(c => c._included && c.isDeductible).length,
    nonDeductible: candidates.filter(c => c._included && !c.isDeductible).length,
  };
  const totalAmount = candidates.filter(c => c._included)
    .reduce((s, c) => s + (Number(c.supply) || 0) + (Number(c.vat) || 0), 0);

  const handleSave = async () => {
    const toSave = candidates.filter(c => c._included).map(candidateToTransaction);
    if (toSave.length === 0) { alert(L("저장할 항목이 없습니다.")); return; }
    setSaving(true);
    try {
      await onSave(toSave);
      onClose && onClose();
    } catch (e) {
      console.error(e);
      alert(L("저장 실패: {0}", e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, padding: 12,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: 1000, maxWidth: '100%',
        maxHeight: '94vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 10px 50px rgba(0,0,0,0.25)',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee',
                      display: 'flex', alignItems: 'center', gap: 14, background: '#f9fafb' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{L("📊 홈택스 엑셀 일괄 등록")}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              {L("전자세금계산서·전자계산서·현금영수증·사업용카드 엑셀 동시 업로드")}
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: 'transparent', fontSize: 22,
            color: '#9ca3af', cursor: 'pointer',
          }}>×</button>
        </div>

        <div style={{ padding: 18, overflow: 'auto', flex: 1, background: '#fafafa' }}>
          {/* 홈택스 엑셀 다운로드 안내 (접이식) */}
          <div style={{
            background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
            marginBottom: 12, overflow: 'hidden',
          }}>
            <button type="button" onClick={() => setGuideOpen(v => !v)}
              style={{
                width: '100%', padding: '12px 14px', border: 'none',
                background: '#FAFAFA', color: '#18181B',
                fontSize: 13, fontWeight: 800, cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
              <span>{L("💡 홈택스에서 엑셀 받는 방법 (4가지)")}</span>
              <span style={{ fontSize: 12 }}>{guideOpen ? L("▲ 접기") : L("▼ 펼치기")}</span>
            </button>
            {guideOpen && (
              <div style={{ padding: '12px 16px', fontSize: 12, lineHeight: 1.7, color: '#1f2937' }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, color: '#18181B', marginBottom: 3 }}>{L("💳 사업용 신용카드 사용내역")}</div>
                  <div style={{ paddingLeft: 6, color: '#4b5563' }}>
                    {L("홈택스 → 조회/발급 → 현금영수증·신용카드 →")} <b>{L("사업용신용카드 매입세액 공제 확인/변경")}</b><br/>
                    {L("분기별 조회 → 우측")} <b>{L("\"내려받기\"")}</b> {L("클릭")}
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, color: '#18181B', marginBottom: 3 }}>{L("🧾 현금영수증 매입내역")}</div>
                  <div style={{ paddingLeft: 6, color: '#4b5563' }}>
                    {L("홈택스 → 조회/발급 → 현금영수증·신용카드 →")} <b>{L("현금영수증 매입내역 조회")}</b><br/>
                    {L("분기별 조회 → 우측")} <b>{L("\"내려받기\"")}</b> {L("클릭")}
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, color: '#18181B', marginBottom: 3 }}>{L("📋 전자세금계산서 매입")}</div>
                  <div style={{ paddingLeft: 6, color: '#4b5563' }}>
                    {L("홈택스 → 조회/발급 → 전자세금계산서 →")} <b>{L("매입 합계표 조회/내려받기")}</b><br/>
                    {L("분류:")} <b>{L("전자세금계산서")}</b> {L("· 매입 선택 · 반기별 조회 →")} <b>{L("\"엑셀내려받기\"")}</b> {L("클릭")}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#18181B', marginBottom: 3 }}>{L("🌱 전자계산서 매입 (면세)")}</div>
                  <div style={{ paddingLeft: 6, color: '#4b5563' }}>
                    {L("홈택스 → 조회/발급 → 전자세금계산서 →")} <b>{L("매입 합계표 조회/내려받기")}</b><br/>
                    {L("분류에서")} <b>{L("전자계산서")}</b> {L("선택 · 매입 · 반기별 조회 →")} <b>{L("\"엑셀내려받기\"")}</b> {L("클릭")}
                  </div>
                </div>
                <div style={{
                  marginTop: 12, padding: '8px 10px', borderRadius: 6,
                  background: '#FAFAFA', border: '1px dashed #D4D4D8',
                  fontSize: 11, color: '#78350f', lineHeight: 1.6,
                }}>
                  {L("⚠ 사업용 카드는 홈택스에서")} <b>{L("공제/불공제 결정")}</b>{L("을 먼저 정리한 후 받으세요.")}<br/>
                  {L("여기서 공제로 변경한 행이 셈핏에 정확히 반영됩니다.")}
                </div>
              </div>
            )}
          </div>

          {/* 파일 선택 + 드래그앤드롭 영역 */}
          <label htmlFor="excel-input"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation();
              setDragOver(false);
              const dropped = e.dataTransfer && e.dataTransfer.files;
              if (dropped && dropped.length > 0) handleFiles(dropped);
            }}
            style={{
              display: 'block', padding: '28px 20px', borderRadius: 10,
              border: dragOver ? '2px dashed #EA580C' : '2px dashed #FF6F0F',
              background: dragOver ? '#F4F4F5' : '#FAFAFA',
              textAlign: 'center', cursor: 'pointer', marginBottom: 16,
              transition: 'all 0.15s',
              transform: dragOver ? 'scale(1.01)' : 'scale(1)',
            }}>
            <div style={{ fontSize: 32, marginBottom: 6 }}>{dragOver ? '📤' : '📥'}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#18181B' }}>
              {dragOver ? L("여기에 놓으면 등록됩니다") : L("홈택스 엑셀 파일을 끌어다 놓거나 클릭")}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              {L(".xls / .xlsx · 여러 개 동시 가능 · 양식 자동 감지")}
            </div>
            <input id="excel-input" type="file" accept=".xls,.xlsx" multiple
              onChange={(e) => handleFiles(e.target.files)}
              style={{ display: 'none' }} />
          </label>

          {parsing && (
            <div style={{ textAlign: 'center', padding: 12, color: '#6b7280' }}>{L("엑셀 파싱 중...")}</div>
          )}

          {files.length > 0 && (
            <div style={{ marginBottom: 16, padding: 10, background: '#fff',
                          borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#374151' }}>
                {L("업로드된 파일 ({0}개)", files.length)}
              </div>
              {files.map((f, idx) => (
                <div key={idx} style={{ fontSize: 12, padding: '4px 0', color: '#4b5563',
                                        display: 'flex', justifyContent: 'space-between' }}>
                  <span>{f.status === 'done' ? FORMAT_LABEL[f.parsed.format] : '❌'} {f.file.name}</span>
                  <span style={{ color: '#9ca3af' }}>
                    {f.status === 'done' ? L("{0}건", f.parsed.candidates.length) : f.error}
                  </span>
                </div>
              ))}
            </div>
          )}

          {candidates.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              <Stat label={L("전체")} value={stats.total} color="#374151" />
              <Stat label={L("✓ 포함")} value={stats.included} color="#FF6F0F" />
              <Stat label={L("✓ 적격")} value={stats.deductible} color="#0369a1" />
              <Stat label={L("⚠ 불공제")} value={stats.nonDeductible} color="#52525B" />
              <Stat label={L("🔄 중복")} value={stats.duplicates} color="#52525B" />
              <Stat label={L("합계")} value={M(totalAmount)} color="#EA580C" wide />
            </div>
          )}

          {partnerGroups.length > 0 && (
            <div style={{
              background: '#fff', borderRadius: 10, overflow: 'hidden',
              border: '1px solid #93c5fd', marginBottom: 14,
            }}>
              <div style={{ padding: '10px 14px', background: '#eff6ff',
                            fontSize: 12, fontWeight: 700, color: '#0369a1',
                            borderBottom: '1px solid #93c5fd' }}>
                {L("📋 공급자별 일괄 분류 — 같은 거래처 ({0}개)", partnerGroups.length)}
                <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2, color: '#475569' }}>
                  {L("카테고리 변경 또는 사업 무관 거래처는 \"🚫 모두 제외\" 클릭")}
                </div>
              </div>
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {partnerGroups.map(g => (
                  <GroupRow key={g.key} group={g}
                    onChange={(major, sub) => bulkUpdateByPartner(g.key, major, sub)}
                    onToggle={(included) => bulkToggleByPartner(g.key, included)} />
                ))}
              </div>
            </div>
          )}

          {candidates.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden',
                          border: '1px solid #e5e7eb' }}>
              <div style={{ padding: '10px 14px', background: '#FAFAFA',
                            fontSize: 11, fontWeight: 700, color: '#78350f',
                            borderBottom: '1px solid #D4D4D8' }}>
                {L("💡 체크 해제하면 저장에서 제외 · 위에서 공급자별 일괄 분류 후 행별 미세 조정 가능")}
              </div>
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {candidates.map(c => (
                  <CandidateRow key={c._id} c={c}
                    onToggle={() => toggleIncluded(c._id)}
                    onCategory={(major, sub) => updateCategory(c._id, major, sub)} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #eee',
                      display: 'flex', gap: 10, justifyContent: 'space-between',
                      alignItems: 'center', background: '#fff' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {stats.included > 0
              ? L("✓ {0}건 저장 예정 ({1})", stats.included, M(totalAmount))
              : L("저장할 항목을 선택하세요")}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving} style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid #ddd',
              background: '#fff', color: '#555', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>{L("취소")}</button>
            <button onClick={handleSave} disabled={saving || stats.included === 0}
              style={{
                padding: '10px 22px', borderRadius: 8, border: 'none',
                background: stats.included === 0 ? '#9ca3af' : '#FF6F0F',
                color: '#fff', fontSize: 13, fontWeight: 800,
                cursor: stats.included === 0 ? 'not-allowed' : 'pointer',
              }}>
              {saving ? L("저장 중...") : L("💾 {0}건 저장", stats.included)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupRow({ group, onChange, onToggle }) {
  const major = group.currentCat?.major || 'material';
  const sub = group.currentCat?.sub || 'raw_material';
  const subOpts = CATEGORY_SUB[major] || [];
  const allExcluded = group.includedCount === 0;
  const partiallyIncluded = group.includedCount > 0 && group.includedCount < group.count;
  return (
    <div style={{
      padding: '8px 14px', borderBottom: '1px solid #f3f4f6',
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: allExcluded ? '#fef2f2' : '#fff',
      opacity: allExcluded ? 0.65 : 1,
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                       textDecoration: allExcluded ? 'line-through' : 'none' }}>
          {group.partnerName}
        </div>
        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
          {L("{0}건", group.count)}
          {partiallyIncluded ? L(" ({0}건 포함)", group.includedCount) : ''}
          {allExcluded ? L(" · 🚫 모두 제외됨") : ''}
          {group.mixed && !allExcluded ? L(" · ⚠ 분류 혼재") : ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
        <select value={major} onChange={e => onChange(e.target.value, null)} disabled={allExcluded}
          style={{ padding: '6px 8px', fontSize: 12, borderRadius: 6,
                    border: '1px solid #93c5fd', background: '#fff', fontWeight: 600, maxWidth: 120,
                    cursor: allExcluded ? 'not-allowed' : 'pointer' }}>
          {CATEGORY_MAJOR.map(m => (
            <option key={m.id} value={m.id}>{Lv(m.label)}</option>
          ))}
        </select>
        <select value={sub || ''} onChange={e => onChange(major, e.target.value || null)} disabled={allExcluded}
          style={{ padding: '6px 8px', fontSize: 12, borderRadius: 6,
                    border: '1px solid #93c5fd', background: '#fff', fontWeight: 600, maxWidth: 140,
                    cursor: allExcluded ? 'not-allowed' : 'pointer' }}>
          <option value="">{L("(소분류)")}</option>
          {subOpts.map(s => (
            <option key={s.id} value={s.id}>{Lv(s.label)}</option>
          ))}
        </select>
        {allExcluded ? (
          <button type="button" onClick={() => onToggle(true)}
            title={L("이 거래처의 모든 거래를 다시 포함")}
            style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700,
                      borderRadius: 6, border: '1px solid #FF6F0F',
                      background: '#fff', color: '#18181B', cursor: 'pointer', flexShrink: 0 }}>
            {L("↩ 복구")}
          </button>
        ) : (
          <button type="button" onClick={() => onToggle(false)}
            title={L("이 거래처의 모든 거래를 저장에서 제외 (사업과 무관)")}
            style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700,
                      borderRadius: 6, border: '1px solid #fca5a5',
                      background: '#fff', color: '#52525B', cursor: 'pointer', flexShrink: 0 }}>
            {L("🚫 모두 제외")}
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, wide }) {
  return (
    <div style={{
      padding: '6px 12px', background: '#fff', borderRadius: 7,
      border: `1px solid ${color}33`, minWidth: wide ? 130 : 90,
    }}>
      <div style={{ fontSize: 10, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function CandidateRow({ c, onToggle, onCategory }) {
  const amt = (Number(c.supply) || 0) + (Number(c.vat) || 0);
  const major = c.category?.major || 'material';
  const sub = c.category?.sub || 'raw_material';
  const subOpts = CATEGORY_SUB[major] || [];

  return (
    <div style={{
      padding: '10px 14px', borderBottom: '1px solid #f3f4f6',
      display: 'flex', alignItems: 'center', gap: 10,
      opacity: c._included ? 1 : 0.5,
      background: c._duplicate ? '#fef2f2' : (c._included ? '#fff' : '#f9fafb'),
    }}>
      <input type="checkbox" checked={c._included} onChange={onToggle}
        style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>
            {c.date?.slice(5).replace('-', '/')}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a',
                          overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>
            {c.partnerName}
          </span>
          <Badge type={c.evidenceType} qualified={c.isDeductible} />
          {c._duplicate && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#52525B',
                          padding: '2px 6px', borderRadius: 4, background: '#F4F4F5' }}>
              {L("🔄 중복")}
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
          {L("공급가 {0} + 부가세 {1} = {2}", fmt(c.supply), fmt(c.vat), M(amt))}
          {c.partnerBizNumber && ` · ${c.partnerBizNumber}`}
          {c.deductibleNote && ` · ${c.deductibleNote}`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <select value={major} onChange={e => onCategory(e.target.value, null)}
          style={{ padding: '4px 6px', fontSize: 11, borderRadius: 5,
                    border: '1px solid #ddd', maxWidth: 100 }}>
          {CATEGORY_MAJOR.map(m => (
            <option key={m.id} value={m.id}>{Lv(m.label)}</option>
          ))}
        </select>
        <select value={sub || ''} onChange={e => onCategory(major, e.target.value || null)}
          style={{ padding: '4px 6px', fontSize: 11, borderRadius: 5,
                    border: '1px solid #ddd', maxWidth: 120 }}>
          <option value="">{L("(소분류)")}</option>
          {subOpts.map(s => (
            <option key={s.id} value={s.id}>{Lv(s.label)}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Badge({ type, qualified }) {
  const meta = {
    card:         { label: L("💳 카드"),       bg: qualified ? '#F4F4F5' : '#F4F4F5', fg: qualified ? '#9A3412' : '#52525B' },
    cash_receipt: { label: L("🧾 현금영수증"),  bg: qualified ? '#F4F4F5' : '#F4F4F5', fg: qualified ? '#9A3412' : '#52525B' },
    tax_invoice:  { label: L("📋 세금계산서"),  bg: '#F4F4F5', fg: '#9A3412' },
  }[type] || { label: type, bg: '#f3f4f6', fg: '#6b7280' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px',
                    borderRadius: 4, background: meta.bg, color: meta.fg }}>
      {Lv(meta.label)}{qualified ? ' ✓' : ''}
    </span>
  );
}
